import { format, isValid, parseISO } from "date-fns";
import { pl } from "date-fns/locale";

import {
  cubeCustomersViewFirstName,
  cubeCustomersViewLastName,
  cubeCustomersViewLoyaltyJoinedAt,
  cubeCustomersViewName,
  cubeCustomersViewPhoneNumber,
  cubeCustomersViewRegion,
  cubeCustomersViewUuid,
  cubeCustomersViewIsPro,
} from "./cubeCustomersViewModel";
import { crmHomeCustomersViewActiveFilters } from "./crmHomeActiveCustomerFilters";
import { fetchCubeLoad, getCubeDataOrThrow } from "./fetchCubeLoad";
import type { CubeFilter, CubeLoadQuery } from "./cubeQueryTypes";

export type UnassignedProRow = {
  uuid: string;
  name: string;
  joinedLabel: string;
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const readString = (v: unknown): string => (typeof v === "string" ? v : "");

const formatJoined = (v: unknown): string => {
  if (v === null || v === undefined) {
    return "—";
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    const d = new Date(v);
    if (isValid(d)) {
      return format(d, "d MMMM yyyy, HH:mm", { locale: pl });
    }
    return String(v);
  }
  if (v instanceof Date && isValid(v)) {
    return format(v, "d MMMM yyyy, HH:mm", { locale: pl });
  }
  if (typeof v === "string") {
    if (v.length === 0) {
      return "—";
    }
    const parsed = parseISO(v);
    if (isValid(parsed)) {
      return format(parsed, "d MMMM yyyy, HH:mm", { locale: pl });
    }
    return v;
  }
  return String(v);
};

const parseRows = (rows: unknown[]): UnassignedProRow[] => {
  const out: UnassignedProRow[] = [];
  for (const item of rows) {
    if (!isRecord(item)) {
      continue;
    }
    const fullName = readString(item[cubeCustomersViewName]).trim();
    const firstT = readString(item[cubeCustomersViewFirstName]).trim();
    const lastT = readString(item[cubeCustomersViewLastName]).trim();
    const phone = readString(item[cubeCustomersViewPhoneNumber]).trim();
    const fromFirstLast = [firstT, lastT].filter((s) => s.length > 0).join(" ").trim();
    let name = "—";
    if (fullName.length > 0) {
      name = fullName;
    } else if (fromFirstLast.length > 0) {
      name = fromFirstLast;
    } else if (phone.length > 0) {
      name = phone;
    }
    out.push({
      uuid: readString(item[cubeCustomersViewUuid]).trim(),
      name,
      joinedLabel: formatJoined(item[cubeCustomersViewLoyaltyJoinedAt]),
    });
  }
  return out;
};

export const loadUnassignedProCurrentYear = async (params: {
  cubeLoad: (query: CubeLoadQuery) => Promise<unknown>;
}): Promise<UnassignedProRow[]> => {
  const calendarYear = new Date().getFullYear();
  const filters: CubeFilter[] = [
    { member: cubeCustomersViewIsPro, operator: "equals", values: [true] },
    {
      or: [
        { member: cubeCustomersViewRegion, operator: "notSet" },
        { member: cubeCustomersViewRegion, operator: "equals", values: [""] },
      ],
    },
    ...crmHomeCustomersViewActiveFilters,
  ];
  const json = await fetchCubeLoad({
    cubeLoad: params.cubeLoad,
    query: {
      dimensions: [
        cubeCustomersViewUuid,
        cubeCustomersViewName,
        cubeCustomersViewFirstName,
        cubeCustomersViewLastName,
        cubeCustomersViewPhoneNumber,
        cubeCustomersViewLoyaltyJoinedAt,
      ],
      filters,
      timeDimensions: [
        {
          dimension: cubeCustomersViewLoyaltyJoinedAt,
          granularity: "year",
          dateRange: `${calendarYear}-01-01 to ${calendarYear}-12-31`,
        },
      ],
      order: [[cubeCustomersViewLoyaltyJoinedAt, "asc"]],
      limit: 5000,
    },
  });
  const rows = getCubeDataOrThrow(json);
  return parseRows(rows);
};
